// @flow

import * as React from 'react';
import * as ReactDOM from 'react-dom';
import memoize from 'fast-memoize';
import scrollbarSize from 'dom-helpers/scrollbarSize';
import { Paper } from '@material-ui/core';
import { computePosition, autoUpdate, size, flip } from '@floating-ui/dom';
import { useClickOutside } from '../hooks/click-outside.js';
import { useClientPosition } from '../hooks/client-position.js';
import {
  requestAnimationTimeout,
  cancelAnimationTimeout,
} from '../utils/animation-timeout.js';

const useLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export type Orient = {|
  horizontal:
    | 'before-left'
    | 'after-left'
    | 'center'
    | 'stretch'
    | 'before-right'
    | 'after-right',
  vertical:
    | 'before-top'
    | 'after-top'
    | 'center'
    | 'before-bottom'
    | 'after-bottom',
|};

const getBoundsWithMargin = elt => {
  const rect = elt.getBoundingClientRect();
  var style = getComputedStyle(elt);

  return {
    top: rect.top - (Number.parseFloat(style.marginTop) || 0),
    left: rect.left - (Number.parseFloat(style.marginLeft) || 0),
    height:
      rect.height +
      (Number.parseFloat(style.marginTop) || 0) +
      (Number.parseFloat(style.marginBottom) || 0),
    width:
      rect.width +
      (Number.parseFloat(style.marginLeft) || 0) +
      (Number.parseFloat(style.marginRight) || 0),
  };
};

type Props<T> = {|
  timeout?: number,
  orient?: Orient | $ReadOnlyArray<Orient>,
  // Specifies how close to the edge of the window the popover can appear.
  marginThreshold?: number,
  hasPointerEvents?: boolean,

  targetRef: {| current: React.ElementRef<T> | null |},
  open: boolean,
  onClose: () => void,
  children: React.Node,
  scrollCloseDelta?: number,
  initialAnimation?: boolean,
|};

const Fade = ({
  children,
  orient,
  fadeRef,
  minWidth,
  left,
  top,
  hasPointerEvents,
  open,
  // style,
  timeout,
}) => {
  const originX = {
    'before-left': 'right',
    'after-left': 'left',
    stretch: 'left',
    center: 'center',
    'before-right': 'right',
    'after-right': 'left',
  }[orient.horizontal];

  const originY = {
    'before-top': 'bottom',
    'after-top': 'top',
    center: 'center',
    'before-bottom': 'bottom',
    'after-bottom': 'top',
  }[orient.vertical];

  const START_X_SCALE = 0.95;
  const START_Y_SCALE = START_X_SCALE ** 2;

  const closeStyle = {
    opacity: 0,
    transform: `scale(${START_X_SCALE}, ${START_Y_SCALE})`,
    transition: `transform ${timeout}ms ease-out, opacity ${timeout}ms ease-out`,
  };

  const openStyle = {
    opacity: 1,
    transform: `scale(1, 1)`,
    transition: `transform ${timeout}ms ease-out, opacity ${timeout}ms ease-out`,
  };

  return (
    <div
      // className={animClass}
      style={open ? openStyle : closeStyle}
      ref={fadeRef}
      css={[
        { minWidth: 'auto' },
        {
          position: 'fixed',
          // listing dialog has zIndex 11
          zIndex: 2,
          left: `${left}px`,
          top: `${top}px`,
          minWidth,
          // opacity,
          transformOrigin: `${originX} ${originY}`,
        },
        hasPointerEvents ? null : { pointerEvents: 'none' },
      ]}
    >
      {children}
    </div>
  );
};

const getPosAndOffset = (bounds, constraints, marginThreshold, state) => {
  const { contentSize, d } = state;

  const l = Math.round(bounds.left + d.x);
  const t = Math.round(bounds.top + d.y);
  const width = contentSize ? contentSize.width : 0;
  const height = contentSize ? contentSize.height : 0;

  const r = l + width;
  const b = t + height;

  const dl = Math.max(0, marginThreshold - l);
  const dr = -Math.max(0, r - (constraints.width - marginThreshold));

  const dt = Math.max(0, marginThreshold - t);
  const db = -Math.max(0, b - (constraints.height - marginThreshold));

  const widthFit = width < constraints.width;
  const heightFit = height < constraints.height;

  const wk =
    (dl !== 0 ? 1 : 0) + (dr !== 0 ? 1 : 0) + (dl === 0 && dr === 0 ? 1 : 0);

  const hk =
    (dt !== 0 ? 1 : 0) + (db !== 0 ? 1 : 0) + (dt === 0 && db === 0 ? 1 : 0);

  return {
    dx: widthFit ? (dl + dr) * (1 / wk) : -l,
    dy: heightFit ? (dt + db) * (1 / hk) : -t,
    x: widthFit ? l + (dl + dr) * (1 / wk) : 0,
    y: heightFit ? t + (dt + db) * (1 / hk) : 0,
  };
};

const getContentSizeAndOrientOffset = (orient, bounds, width, height) => {
  const contentSize = {
    width: orient.horizontal === 'stretch' ? bounds.width : width,
    height,
  };

  // find best orientation
  const x = {
    'before-left': -width,
    'after-left': 0,
    stretch: 0,
    center: (bounds.width - width) / 2,
    'before-right': bounds.width - width,
    'after-right': bounds.width,
  }[orient.horizontal];

  const y = {
    'before-top': -height,
    'after-top': 0,
    center: (bounds.height - height) / 2,
    'before-bottom': bounds.height - height,
    'after-bottom': bounds.height,
  }[orient.vertical];

  return {
    contentSize,
    d: { x, y },
    orient,
  };
};

const PopupContent = ({
  open,
  hasPointerEvents,
  bounds,
  constraints,
  marginThreshold,
  timeout,
  orients,
  children,
}) => {
  const [state, setState] = React.useState({
    contentSize: null,
    d: { x: 0, y: 0 },
    orient: orients[0],
  });

  const fadeRef = React.useRef(null);

  const posAndOffset = getPosAndOffset(
    bounds,
    constraints,
    marginThreshold,
    state,
  );

  const left = posAndOffset.x;
  const top = posAndOffset.y;

  // measure size at onEnter handler because:
  // until all browsers will support width: fit-content
  // we need to measure popover size when element is placed at 0, 0 corner
  // otherwise fixed element size is highly affected by screen size and position
  // see https://codepen.io/istarkov/pen/pxpNRz
  const handleFadeCreate = React.useCallback(
    bounds => {
      if (fadeRef.current == null) {
        return;
      }

      const height = fadeRef.current.clientHeight;
      const width = fadeRef.current.clientWidth;

      let possibleState = getContentSizeAndOrientOffset(
        orients[0],
        bounds,
        width,
        height,
      );

      let minOffset = getPosAndOffset(
        bounds,
        constraints,
        marginThreshold,
        possibleState,
      );

      for (let i = 1; i < orients.length; i += 1) {
        const nextPossibleState = getContentSizeAndOrientOffset(
          orients[i],
          bounds,
          width,
          height,
        );
        const nextOffset = getPosAndOffset(
          bounds,
          constraints,
          marginThreshold,
          nextPossibleState,
        );

        if (
          Math.abs(nextOffset.dx) + Math.abs(nextOffset.dy) <
          Math.abs(minOffset.dx) + Math.abs(minOffset.dy)
        ) {
          possibleState = nextPossibleState;
          minOffset = nextOffset;
        }
      }

      setState(possibleState);
    },
    [orients, constraints, marginThreshold],
  );

  React.useEffect(() => {
    handleFadeCreate(bounds);
  }, [bounds, handleFadeCreate]);

  const body =
    typeof window !== 'undefined' && document.body ? document.body : null;

  if (body) {
    return ReactDOM.createPortal(
      open && (
        <Fade
          hasPointerEvents={hasPointerEvents}
          fadeRef={fadeRef}
          orient={state.orient}
          minWidth={state.contentSize ? state.contentSize.width : 'fit-content'}
          open={open && state.contentSize != null && fadeRef.current != null}
          left={left}
          top={top}
          timeout={timeout}
        >
          {children()}
        </Fade>
      ),
      body,
    );
  }
  return null;
};

const getMemoizedOrientation = memoize(
  (orient?: Orient | $ReadOnlyArray<Orient>): $ReadOnlyArray<Orient> =>
    orient == null
      ? [{ horizontal: 'after-left', vertical: 'after-bottom' }]
      : Array.isArray(orient)
      ? orient
      : [orient],
);

// Used only by map popup. DO NOT USE. Implement something else.
export const Popup = <T: string>({
  timeout = 250,
  hasPointerEvents = true,
  orient,
  marginThreshold = 16,
  scrollCloseDelta = 0,
  targetRef,
  open,
  onClose,
  children,
  initialAnimation,
}: Props<T>): React.Node => {
  const orientation: $ReadOnlyArray<Orient> = React.useMemo(
    () => getMemoizedOrientation(orient),
    [orient],
  );

  const [state, update] = React.useReducer(
    () => {
      const bounds =
        targetRef.current != null
          ? getBoundsWithMargin(targetRef.current)
          : { left: 0, top: 0, width: 0, height: 0 };

      const barSize = scrollbarSize();

      const bottomBarSize =
        (document.body?.scrollWidth ?? 0) > (document.body?.clientWidth ?? 0)
          ? barSize
          : 0;
      const rightBarSize =
        (document.body?.scrollHeight ?? 0) > (document.body?.clientHeight ?? 0)
          ? barSize
          : 0;

      // We use window dimensions here to get right size on mobile, as usually body dimensions differs
      // from window dimensions
      const constraints = {
        left: 0,
        top: 0,
        width: window.innerWidth - rightBarSize,
        height: window.innerHeight - bottomBarSize,
      };

      return { bounds, constraints };
    },
    {
      bounds: { left: 0, top: 0, width: 0, height: 0 },
      constraints: { left: 0, top: 0, width: 0, height: 0 },
    },
  );

  const popupRef = React.useRef(null);
  const onCloseRef = React.useRef(onClose);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useClickOutside([targetRef, popupRef], onClose);
  useClientPosition(targetRef, open, update);

  const prevPageYOffsetRef = React.useRef(0);

  // This fixes chrome bug, when on history back if page has scroll
  // getBoundingClientRect returns negative position for elements
  React.useEffect(() => {
    const handle = requestAnimationTimeout(0, update);
    return () => cancelAnimationTimeout(handle);
  }, []);

  React.useEffect(() => {
    if (open) {
      update();

      const handleScroll = () => {
        if (
          Math.abs(window.pageYOffset - prevPageYOffsetRef.current) >
            scrollCloseDelta &&
          // IOS hack, see FilterDialog
          window.document.body.dataset.iosDialogIsOpen !== 'true'
        ) {
          // onCloseRef.current();

          prevPageYOffsetRef.current = window.pageYOffset;
        }
      };

      prevPageYOffsetRef.current = window.pageYOffset;

      window.addEventListener('scroll', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [open, scrollCloseDelta]);

  return (
    <PopupContent
      timeout={timeout}
      hasPointerEvents={hasPointerEvents}
      marginThreshold={marginThreshold}
      open={open}
      bounds={state.bounds}
      constraints={state.constraints}
      orients={orientation}
      initialAnimation={initialAnimation}
      // onClosed={}
    >
      {() => <div ref={popupRef}>{children}</div>}
    </PopupContent>
  );
};

export const Portal = ({
  children,
}: {|
  children: React.Node,
|}): React.Node => {
  if (typeof document !== 'undefined' && document.body) {
    return ReactDOM.createPortal(children, document.body);
  } else {
    // popup should be always rendered to prevent mismatches between ssr and client
    return children;
  }
};

export const InputPopup = ({
  referenceRef,
  open,
  className,
  css: cssProp,
  children,
}: {|
  referenceRef: {| current: HTMLElement | null |},
  open: boolean,
  className?: string,
  css?: any,
  children: React.Node,
|}): React.Node => {
  const popupRef = React.useRef(null);
  React.useEffect(() => {
    const reference = referenceRef.current;
    const popup = popupRef.current;
    if (open && reference != null && popup != null) {
      const update = () => {
        computePosition(reference, popup, {
          middleware: [
            flip(),
            size({
              apply: ({ availableHeight, elements }) => {
                const { reference } = elements;
                const rect = reference.getBoundingClientRect();
                popup.style.width = `${rect.width}px`;
                popup.style.maxHeight = `${availableHeight}px`;
              },
            }),
          ],
        }).then(({ strategy, x, y }) => {
          popup.style.position = strategy;
          popup.style.left = `${x}px`;
          popup.style.top = `${y}px`;
        });
      };
      update();
      const cleanup = autoUpdate(reference, popup, update);
      return cleanup;
    }
  }, [open, referenceRef]);

  return (
    <Portal>
      {/* $FlowFixMe[prop-missing] */}
      <Paper
        ref={popupRef}
        elevation={3}
        className={className}
        css={[
          {
            display: open ? 'flex' : 'none',
            // overlap input labels
            zIndex: 1300,
            '> *': {
              width: '100%',
              // add vertical scroll to child list to enable scrollIntoView
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            },
          },
          cssProp,
        ]}
        // TODO remove when flare InteractOutside is out
        data-invisible-for-click-outside={true}
      >
        {children}
      </Paper>
    </Portal>
  );
};
